import { DispatchManifestStopDetailStatus } from "app/data/city-delivery/dispatch-board/models";
import { PickupStatus } from "app/data/common/route";
import type { Pickup } from "slices/pickup/UnassignedPickupState";
import type { FreightItemForDisplay } from "../../common/pickup-details/pickupFreightItemsBlock";

/** How static-route pickups are bucketed into unassigned stop groups (V2 column + map). */
export type UnassignedGroupByMode = "shipper_address" | "shipper_name";

export const DEFAULT_UNASSIGNED_GROUP_BY: UnassignedGroupByMode = "shipper_address";

export function sanitizeUnassignedGroupBy(raw: unknown): UnassignedGroupByMode {
  if (raw === "shipper_name") return "shipper_name";
  return "shipper_address";
}

/** Single pickup row (API-mapped) used in unassigned lists. */
export type UnassignedPickup = {
  id: string;
  title: string;
  address: string;
  timeRange: string;
  type: "Delivery" | "Pickup";
  puNumber: string;
  consigneeName: string;
  pieces: number;
  weight: string;
  eta: string;
  shipperName: string;
  date: string;
  status: string[];
  probillStatus: string | null;
  zip: string;
  probillNumber: number | null;
  /** Raw schedule fields for At Risk / Late map borders */
  pickupDateForTiming: string;
  readyTimeForTiming: string;
  closeTimeForTiming: string;
  rollsUnits: number;
  palletsUnits: number;
  totalItemsWeight: number;
  pickupWindowLine: string;
  shipperContactLine: string;
  consigneeAddressLine: string;
  consigneeContactLine: string;
  itemLines: FreightItemForDisplay[];
  /** Consignee name, or "City, ST" when name is empty — for shipper → consignee summary. */
  consigneeSummaryLabel: string;
};

/** Grouped stop within a static route (bucket = address+window or name+window per V2 “Group by”). */
export type UnassignedStopGroup = {
  id: string;
  pickups: UnassignedPickup[];
  groupStopStatus: DispatchManifestStopDetailStatus;
  /** Shipper coordinates for map (first pickup in group). */
  lat?: number;
  lng?: number;
};

export type UnassignedRoute = {
  id: string;
  name: string;
  count: number;
  stops: UnassignedStopGroup[];
};

export function aggregateUnassignedGroupStatus(
  pickups: UnassignedPickup[]
): DispatchManifestStopDetailStatus {
  const completed = pickups.filter((p) => p.status[0] === PickupStatus.COMPLETED).length;
  const total = pickups.length;
  if (total === 0) return DispatchManifestStopDetailStatus.PENDING;
  if (completed === total) return DispatchManifestStopDetailStatus.COMPLETED;
  if (completed > 0) return DispatchManifestStopDetailStatus.IN_PROGRESS;
  return DispatchManifestStopDetailStatus.PENDING;
}

function trimToken(v: string | null | undefined): string {
  if (v == null) return "";
  const t = String(v).trim();
  if (t === "" || t.toLowerCase() === "null" || t.toLowerCase() === "undefined") return "";
  return t;
}

/**
 * Consignee label for compact UI: name when present, otherwise "City, State".
 */
export function consigneeSummaryFromPickupFields(
  consigneeName: string | null | undefined,
  consigneeCity: string | null | undefined,
  consigneeState: string | null | undefined
): string {
  const name = trimToken(consigneeName ?? undefined);
  if (name) return name;
  const city = trimToken(consigneeCity ?? undefined);
  const state = trimToken(consigneeState ?? undefined);
  const cityState = [city, state].filter(Boolean).join(", ");
  return cityState || "—";
}

export type PickupWindowGroupKeyFields = {
  shipperAddress1: string;
  shipperCity: string;
  shipperState: string;
  shipperPostalCode: string;
  pickupDate: string;
  pickupReadyTime: string;
  pickupCloseTime: string;
};

/** Stable key: shipper address + pickup date + window start/end (same grouping for unassigned + assigned columns). */
export function pickupWindowGroupKey(f: PickupWindowGroupKeyFields): string {
  const addr = [
    trimToken(f.shipperAddress1),
    trimToken(f.shipperCity),
    trimToken(f.shipperState),
    trimToken(f.shipperPostalCode),
  ]
    .join("|")
    .toLowerCase();
  return [
    addr,
    trimToken(f.pickupDate),
    trimToken(f.pickupReadyTime),
    trimToken(f.pickupCloseTime),
  ].join("|");
}

export function staticRoutePickupGroupKey(p: Pickup): string {
  return pickupWindowGroupKey({
    shipperAddress1: p.shipperAddress1,
    shipperCity: p.shipperCity,
    shipperState: p.shipperState,
    shipperPostalCode: p.shipperPostalCode,
    pickupDate: p.pickupDate,
    pickupReadyTime: p.pickupReadyTime,
    pickupCloseTime: p.pickupCloseTime,
  });
}

/** Group pickups by shipper display name + same pickup window fields as address grouping. */
export function shipperNamePickupWindowGroupKey(p: Pickup): string {
  const name = trimToken(p.shipperName) || "__no_shipper__";
  return [
    name.toLowerCase(),
    trimToken(p.pickupDate),
    trimToken(p.pickupReadyTime),
    trimToken(p.pickupCloseTime),
  ].join("|");
}

export function staticRoutePickupGroupKeyForMode(p: Pickup, mode: UnassignedGroupByMode): string {
  if (mode === "shipper_name") return shipperNamePickupWindowGroupKey(p);
  return staticRoutePickupGroupKey(p);
}
